import { useTranslation } from 'react-i18next';
import { ArrowUpDown, Info, X } from 'lucide-react';
import { formatLiczby } from '@/lib/i18nNumber';
import { useAnimatedCount } from '@/hooks/useAnimatedCount';

/**
 * Nagłówek wyszukiwarki na telefonie — TEN SAM dla listy i dla mapy.
 *
 * DLACZEGO DWIE LINIJKI ZAMIAST TRZECH RZĘDÓW. W widoku mapy pole wyszukiwania, rząd licznika
 * i rząd chipów zajmowały razem ~180 px z 659 px ekranu, czyli ponad jedną czwartą, a mapie
 * zostawało 314 px. Każdy z tych rzędów odpowiadał na inne pytanie i żaden nie odpowiadał na
 * to, które człowiek ma przed sobą: „czego właściwie szukam i ile tego jest".
 *
 * DLACZEGO TERAZ TAKŻE NA LIŚCIE (decyzja Piotra 27.09). Lista miała własny nagłówek: pole
 * wyszukiwania, rząd chipów i rząd licznika. Przełączenie Lista ↔ Mapa zmieniało więc nie
 * tylko treść pod spodem, ale i cały sposób sterowania — te same filtry wyglądały inaczej po
 * obu stronach jednego przycisku. Jeden komponent (nie kopia) znaczy, że każda zmiana
 * nagłówka dzieje się w obu widokach naraz i nie da się ich rozjechać.
 *
 * Wzór QuintoAndar: jedna linia mówi GDZIE („Warszawa · Muranów, Wilanów"), druga ILE i CZY SĄ
 * FILTRY („145 ofert · bez filtrów"). Dotknięcie tekstu otwiera wybór obszaru — i tam, w
 * nakładce, jest pole do wpisywania, więc ścieżka „wpisz osiedle" nie ginie razem z polem z
 * listy. Klawiatura NIE wywołuje się sama (zasada Piotra z 26.09: gdzie nie trzeba pisać, nie
 * wywołujemy klawiatury); człowiek dotyka pola w nakładce, gdy chce pisać.
 *
 * Pod spodem JEDEN RZĄD FILTRÓW (propozycja Maćka, Piotr 6.10, `RzadFiltrow`): Cena, Pokoje,
 * sugestie i aktywne chipy, z przypiętymi × (wyczyść wszystko) i suwakiem (wszystkie filtry).
 * Sortowanie ⇅ i zakładka „Moje wyszukiwanie” stoją w karcie.
 */
export function NaglowekMobilny({
  miejsce,
  liczbaOfert,
  notyWyniku = [],
  zProfilu = false,
  liczbaWszystkichFiltrow,
  noty,
  naObszar,
  rzadFiltrow,
  naUsunObszar,
  sortowanie = null,
}: {
  /** „Warszawa · Muranów, Wilanów" — gotowy napis, składany przez stronę. */
  miejsce: string;
  /**
   * Liczba ofert albo `null`, gdy jeszcze LICZYMY. `null` to nie to samo co zero: zero jest
   * odpowiedzią serwera („nic nie pasuje"), a `null` znaczy „nie wiem jeszcze". Do 27.09
   * strona podawała tu `0` w czasie wczytywania i nagłówek na wolnym łączu pokazywał
   * „0 ofert", a po sekundzie skakał na „4 044 oferty" — zgłoszenie Piotra z telefonu.
   */
  liczbaOfert: number | null;
  /**
   * Zdania tłumaczące liczbę wyników (szacowane opłaty, oferty pominięte bez informacji
   * o opłatach). Idą do IKONY, nie do linijki: dwie naraz urywały podsumowanie i druga
   * nie mieściła się wcale (zrzut z 28.09). Ikona dostaje wtedy kropkę.
   */
  notyWyniku?: string[];
  /**
   * Filtry przyszły z profilu („Moje wyszukiwanie", SRCH-01). Na telefonie nie ma rzędu
   * chipów, więc mówi o tym linijka podsumowania — inaczej człowiek widzi zawężony wynik
   * i nie wie, czym został zawężony.
   */
  zProfilu?: boolean;
  /**
   * Zdanie w nagłówku („3 filtry") liczy WSZYSTKIE ustawione filtry poza lokalizacją — także
   * cenę i pokoje. To inna liczba niż znacznik na pastylce „Filtry" (`liczbaFiltrow`), który
   * pomija cenę i pokoje, bo mają własne pastylki. Pomylenie ich dawało „bez filtrów" przy
   * ustawionej cenie (28.09).
   */
  liczbaWszystkichFiltrow: number;
  /** Liczby do ikony informacji; `null` = nie ma o czym mówić. */
  noty: { naMapie: number; brakuje: number; pozaKadrem: number } | null;
  naObszar: () => void;
  /** Wspólny rząd filtrów (`RzadFiltrow`, propozycja Maćka 6.10) pod kartą. */
  rzadFiltrow: React.ReactNode;
  /** Podane tylko, gdy wybrana jest lokalizacja — wtedy blok ma × „wróć do całego miasta". */
  naUsunObszar?: () => void;
  /**
   * ⇅ W KARCIE, obok zakładki „Moje wyszukiwanie” (układ filtrów Maćka 6.10; wcześniej na końcu
   * rzędu skrótów). Otwiera arkusz sortowania. `null` = bez ikony (mapa — nie ma czego sortować).
   */
  sortowanie?: { etykieta: string; aktywne: boolean; naOtworz: () => void } | null;
}) {
  const { t, i18n } = useTranslation();
  // Licznik PŁYNIE do nowej wartości przy każdym przefiltrowaniu (plan E5.1, §1.33) — ta sama
  // zasada co w `h1` na pulpicie; słowo po liczbie bierze formę z wartości końcowej od razu.
  const liczbaPokazywana = useAnimatedCount(liczbaOfert) ?? liczbaOfert;

  return (
    <div className="pb-2 pt-2" data-testid="naglowek-mobilny">
      <div className="flex items-center gap-2 rounded-2xl border bg-white px-3 py-2 shadow-sm">
        <button type="button" onClick={naObszar} className="strefa-dotyku relative min-w-0 flex-1 text-left">
          <span className="block truncate text-sm font-semibold text-foreground">{miejsce}</span>
          {/* LICZNIK JEST NAGŁÓWKIEM PIERWSZEGO POZIOMU także tutaj (SRCH-05). W widoku mapy
              rząd licznika nie istnieje, więc bez tego strona zostawałaby bez żadnego `h1` —
              regresja dostępności i SEO, a przy okazji ślepota bramek, które czytają właśnie
              `h1`. Wygląda jak druga linijka podsumowania i tym jest; rola jest semantyczna,
              nie wizualna. */}
          <h1 className="block truncate text-xs font-normal text-muted-foreground">
            {liczbaOfert == null ? (
              <>
                {/* Szkielet w miejscu liczby: ta sama wysokość linijki, więc układ nie
                    drgnie, gdy liczba wreszcie przyjdzie. `h1` zostaje z tekstem dla
                    czytnika ekranu i dla bramek, które czytają nagłówek. */}
                <span className="sr-only">{t('search.title')}</span>
                <span
                  aria-hidden
                  data-testid="licznik-wczytywanie"
                  className="inline-block h-3 w-24 animate-pulse rounded bg-muted align-middle"
                />
              </>
            ) : (
              <>
                {t('search.result_count', {
                  count: liczbaOfert,
                  liczba: formatLiczby(liczbaPokazywana ?? liczbaOfert, i18n.language),
                })}
                {' · '}
                {zProfilu
                  ? t('search.my_search_chip').toLocaleLowerCase()
                  : liczbaWszystkichFiltrow > 0
                    ? t('search.map.header_filters', { count: liczbaWszystkichFiltrow })
                    : t('search.map.header_no_filters')}

              </>
            )}
          </h1>
        </button>
        {/* ⓘ: w widoku mapy (`noty` podane) miejsce jest ZAWSZE, niewidoczne bez noty — pojawiająca
            się ikona przesuwała ×, ⇅ i zakładkę (Piotr 7.10). Na liście ikona tylko przy nocie. */}
        {(noty != null || notyWyniku.length > 0) && (() => {
          const jest = notyWyniku.length > 0 || !!(noty && (noty.brakuje > 0 || noty.pozaKadrem > 0));
          return (
          <span
            data-testid="nota-w-karcie"
            aria-hidden={!jest || undefined}
            title={[
              ...notyWyniku,
              noty && noty.pozaKadrem > 0 ? t('search.map.outside_view', { count: noty.pozaKadrem }) : null,
            ]
              .filter(Boolean)
              .join('\n')}
            className={`relative flex h-8 w-8 shrink-0 items-center justify-center text-muted-foreground ${jest ? '' : 'invisible'}`}
            aria-label={jest ? t('search.map.notes_aria') : undefined}
          >
            <Info className="h-4 w-4" />
            {/* Kropka, gdy ikona niesie notę o WYNIKU — bez niej informacja jest schowana
                przed kimś, kto nie wie, że ma dotknąć „i". */}
            {notyWyniku.length > 0 && (
              <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary" />
            )}
          </span>
          );
        })()}
        {/* × lokalizacji: miejsce ZAWSZE (Piotr 7.10) — wybór obszaru nie przesuwa ⇅ ani zakładki. */}
        {!naUsunObszar && <span aria-hidden="true" data-testid="usun-lokalizacje-miejsce" className="-mx-1 h-11 w-11 shrink-0" />}
        {naUsunObszar && (
          <button
            type="button"
            data-testid="usun-lokalizacje"
            // × zdejmuje lokalizację i NIE otwiera nakładki obszaru, choć stoi w tym samym bloku.
            onClick={(e) => { e.stopPropagation(); naUsunObszar(); }}
            aria-label={t('search.area.clear_location')}
            // 44 px celu dotyku (zalecenie Apple), choć ikona jest mała.
            className="-mx-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {sortowanie && (
          <button
            type="button"
            onClick={sortowanie.naOtworz}
            data-testid="sortuj-w-karcie"
            aria-label={sortowanie.etykieta}
            title={sortowanie.etykieta}
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-muted ${
              sortowanie.aktywne ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <ArrowUpDown className="h-5 w-5" aria-hidden />
          </button>
        )}
      </div>

      {/* JEDEN RZĄD FILTRÓW (propozycja Maćka 6.10): Cena, Pokoje, sugestie i aktywne chipy,
          z przypiętymi × i suwakiem na końcu (`RzadFiltrow`). Pastylka „Filtry” i cienki wiersz
          „Wyczyść filtry · Sortuj” nad listą zniknęły: suwak i × są tu, ⇅ w karcie. */}
      <div className="pt-2">{rzadFiltrow}</div>
    </div>
  );
}

export default NaglowekMobilny;
